import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { type Attachment, AttachmentsDropzone, Form } from '@faceup/ui'
import { Alert } from '@faceup/ui-base'
import { DEFAULT_LANGUAGE, Plan } from '@faceup/utils'
import { useContext, useMemo } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { LanguageContext } from '../../Contexts/LanguageContext'
import useConfigForProject from '../../hooks/useConfigForProject'
import { sharedMessages } from '../../Shared/translations'

const messages = defineMessages('followUp.pages.createReport.attachmentsDropzoneFormItem', [
  { id: 'attachmentsLabel', defaultMessage: 'Attachments' },
  { id: 'dropzoneDrop', defaultMessage: 'or drag and drop it' },
  { id: 'dropzoneOpen', defaultMessage: 'Select a file' },
  { id: 'dropzoneLimit', defaultMessage: 'Upload up to 20 files (maximum 50 MB each)' },
  { id: 'addAttachment', defaultMessage: 'Upload attachments' },
  {
    id: 'alertMessage',
    defaultMessage:
      'Attachments can break your anonymity, so we always try to remove all metadata from attachments.',
  },
  { id: 'alertLink', defaultMessage: 'Learn more' },
] as const)

const fragments = {
  AttachmentsDropzoneFormItem_institution: graphql(`
    fragment AttachmentsDropzoneFormItem_institution on PublicCompany {
      type
      billing {
        plan
      }
    }
  `),
}

type AttachmentsDropzoneFormItemProps = {
  onSetAttachments: (attachments: Attachment[]) => void
  institution?: FragmentType<typeof fragments.AttachmentsDropzoneFormItem_institution> | null
  attachments: Attachment[]
}

export const AttachmentsDropzoneFormItem = ({
  attachments,
  onSetAttachments,
  institution: _institution,
}: AttachmentsDropzoneFormItemProps) => {
  const institution = getFragmentData(
    fragments.AttachmentsDropzoneFormItem_institution,
    _institution
  )
  const { formatMessage } = useIntl()

  const { getSupportUrlByLanguage } = useConfigForProject()

  const { language } = useContext(LanguageContext)

  // TODO: https://gitlab.com/faceup-nntb/monorepo/-/merge_requests/694#note_1132857869
  // create  and use useTier hook instead of this
  const canUseDropzone = useMemo(
    () =>
      Boolean(institution?.billing?.plan) &&
      [Plan.Basic, Plan.Standard, Plan.Premium].includes(institution?.billing?.plan ?? Plan.Basic),
    [institution?.billing?.plan]
  )

  if (!canUseDropzone) {
    return null
  }

  return (
    <Form.Item
      label={<FormattedMessage message={messages.attachmentsLabel} />}
      formItemProps={{
        'data-test': 'create-company-report-form-attachment-dropzone',
      }}
    >
      <AttachmentsDropzone
        attachments={attachments}
        setAttachments={files => {
          onSetAttachments(files)
        }}
        translations={{
          tooManyFiles: formatMessage(sharedMessages.attachmentsTooManyFiles),
          tooLargeFile: formatMessage(sharedMessages.attachmentsTooLargeFile),
          metadataRemoved: formatMessage(sharedMessages.metadataRemoved),
          metadataUnableToRemove: formatMessage(sharedMessages.metadataUnableToRemove),
          unsupportedMimetype: formatMessage(sharedMessages.unsupportedMimetype),
          delete: formatMessage(sharedMessages.delete),
          dropzoneDrop: formatMessage(messages.dropzoneDrop),
          dropzoneLimits: formatMessage(messages.dropzoneLimit),
          dropzoneOpen: formatMessage(messages.dropzoneOpen),
          add: formatMessage(messages.addAttachment),
        }}
      />
      {attachments.length > 0 && (
        <Alert
          className='mt-4'
          type='info'
          closable
          message={
            <div className='p-1'>
              <FormattedMessage message={messages.alertMessage} />{' '}
              <a
                className='underline'
                href={getSupportUrlByLanguage(language ?? DEFAULT_LANGUAGE)}
                target='_blank'
                rel='noopener noreferrer'
              >
                <FormattedMessage message={messages.alertLink} />
              </a>
            </div>
          }
        />
      )}
    </Form.Item>
  )
}
